/**
 * ModeToggle — the editor-toolbar mode pill (View/Edit, Preview/Source,
 * View/Edit/SQL, …) with a sliding active indicator.
 *
 * The indicator is JS-measured (offsetLeft/offsetWidth of the active
 * button) rather than CSS-guessed, so any number of variable-width
 * options animate correctly, including option sets that change at
 * runtime (e.g. ScriptEditor's Run appearing per permission).
 *
 * Options beyond `maxVisible` fold into a chevron list. If the active
 * option lives in the overflow, it swaps into the last visible slot so
 * the current mode is always visible in the pill.
 */
import { useState, useRef, useEffect, useLayoutEffect, useContext, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { ToolbarPopover } from './ToolbarPopover'
import { ToolbarCompactContext } from './toolbarCompact'
import { labelText } from './ToolbarControls'

export interface ModeOption<T extends string = string> {
  value: T
  label: ReactNode
  icon?: ReactNode
  title?: string
}

/** The hint a compact pill offers for the label CSS just hid. Same rule as the
 *  rest of the toolbar family (`labelText`), so an interpolated label like
 *  `Run {n}` works here too — with the same dev-time complaint when a label has
 *  no honest text form, because the alternative is an icon nobody can name. */
function hiddenLabelHint(label: ReactNode): string | undefined {
  const text = labelText(label)
  if (text === undefined && label != null && import.meta.env.DEV) {
    console.error(
      '[ModeToggle] this option\'s label is not plain text, so it has no hint once the ' +
      'toolbar goes icon-only. Give the option a `title`.',
    )
  }
  return text
}

export function ModeToggle<T extends string>({
  options,
  value,
  onChange,
  maxVisible = 3,
}: {
  options: ModeOption<T>[]
  value: T
  onChange: (value: T) => void
  maxVisible?: number
}) {
  const pillRef = useRef<HTMLDivElement>(null)
  const [slider, setSlider] = useState<{ left: number; width: number } | null>(null)
  // First position paints without a transition so the slider doesn't
  // fly in from 0 on mount
  const [ready, setReady] = useState(false)
  const [listOpen, setListOpen] = useState(false)
  const chevronRef = useRef<HTMLButtonElement>(null)
  // In a compact toolbar an icon option's label is hidden — show it as the hint.
  const compact = useContext(ToolbarCompactContext)

  // Visible set: first maxVisible options, with the active one swapped
  // into the last slot if it would otherwise be hidden
  let visible = options.slice(0, maxVisible)
  if (!visible.some(o => o.value === value)) {
    const active = options.find(o => o.value === value)
    if (active) visible = [...visible.slice(0, maxVisible - 1), active]
  }
  const overflow = options.filter(o => !visible.some(v => v.value === o.value))

  const measure = () => {
    const pill = pillRef.current
    if (!pill) return
    const btn = pill.querySelector<HTMLElement>(`[data-mode="${CSS.escape(value)}"]`)
    if (!btn) { setSlider(null); return }
    setSlider({ left: btn.offsetLeft, width: btn.offsetWidth })
  }
  const measureRef = useRef(measure)
  measureRef.current = measure

  useLayoutEffect(() => { measureRef.current() }, [value, options])
  useEffect(() => {
    // Enable the slide transition only after the first paint has positioned
    // the indicator
    const t = requestAnimationFrame(() => setReady(true))
    const pill = pillRef.current
    const ro = new ResizeObserver(() => measureRef.current())
    if (pill) ro.observe(pill)
    return () => { cancelAnimationFrame(t); ro.disconnect() }
  }, [])

  // When the option set narrows so nothing overflows, the chevron anchor
  // unmounts — reset listOpen so the (unconditionally-rendered) ToolbarPopover
  // doesn't linger open with a null anchor at a stale position.
  useEffect(() => {
    if (listOpen && overflow.length === 0) setListOpen(false)
  }, [listOpen, overflow.length])

  return (
    <div className="relative">
      <div ref={pillRef} className="mode-pill">
        {slider && (
          <span
            className="mode-pill-slider"
            data-ready={ready ? '' : undefined}
            style={{ left: slider.left, width: slider.width }}
          />
        )}
        {visible.map(o => (
          <button
            key={o.value}
            data-mode={o.value}
            onClick={() => onChange(o.value)}
            className={`mode-pill-btn ${o.value === value ? 'active' : ''}`}
            data-tooltip={o.title ?? (compact && o.icon ? hiddenLabelHint(o.label) : undefined)}
          >
            {o.icon}
            {o.icon ? <span className="ws-tb-label">{o.label}</span> : o.label}
          </button>
        ))}
        {overflow.length > 0 && (
          <button
            ref={chevronRef}
            onClick={() => setListOpen(v => !v)}
            className="mode-pill-btn"
            data-tooltip="More modes"
            aria-label="More modes"
            aria-expanded={listOpen}
          >
            <ChevronDown size={12} />
          </button>
        )}
      </div>
      {/* Portaled (not an in-flow absolute div) so the editor card's rounded
          overflow can't clip the list. */}
      {overflow.length > 0 && (
        <ToolbarPopover anchorRef={chevronRef} open={listOpen} onClose={() => setListOpen(false)}>
          {overflow.map(o => (
            <button
              key={o.value}
              onClick={() => { onChange(o.value); setListOpen(false) }}
              className="w-full px-3 py-1.5 text-left text-xs themed-text hover:themed-bg-tertiary transition-colors flex items-center gap-2 rounded"
              data-tooltip={o.title}
            >
              {o.icon}
              {o.label}
            </button>
          ))}
        </ToolbarPopover>
      )}
    </div>
  )
}
